// ============== KokenLocation Bridge (plugin GPS natif Kōken) ==============
// Le tracking GPS background passe exclusivement par `window.kokenLocation`,
// le plugin natif custom Kōken (iOS Swift, Android Kotlin).
//
// Ce fichier est le SEUL point d'entrée pour les types et la détection du
// plugin GPS. Les services GPS (`unifiedLocationService`, `trailNativeLocationService`)
// importent depuis ici, et non depuis `medianNativeBridge`.

/** Un point GPS remonté par le plugin natif Kōken. */
export interface KokenLocationPoint {
  latitude: number;
  longitude: number;
  accuracy?: number;
  speed?: number;
  /** Epoch en secondes — converti en ms côté service. */
  time?: number;
}

/** Payload du callback `start` (un batch de points). */
export interface KokenLocationCallbackData {
  locations: KokenLocationPoint[];
}

/**
 * Contrat du plugin GPS natif Kōken.
 *
 * Les implémentations natives (iOS Swift / Android Kotlin) DOIVENT respecter
 * cette signature à l'identique : c'est le seul moteur GPS de l'app.
 */
export interface KokenBackgroundLocation {
  start: (opts: {
    callback: (data: KokenLocationCallbackData) => void;
    iosDistanceFilter?: number;
    androidSmallestDisplacement?: number;
    /**
     * Filet de résilience : le natif poste AUSSI chaque lot directement à
     * l'edge `receive-gps-points`. Indispensable car quand iOS suspend la
     * WebView le callback JS ne s'exécute plus. Contient le JWT en query string.
     */
    postUrl?: string;
    /**
     * Android seulement, coques récentes : titre et texte de la notification
     * du service de suivi, tant que l'activité en direct n'a pas pris le
     * relais. Ignorés par iOS et par les coques qui ne les connaissent pas.
     */
    androidNotificationTitle?: string;
    androidNotificationText?: string;
  }) => void;
  /**
   * Renouvelle l'URL d'envoi (donc le JWT) SANS interrompre le suivi.
   * Optionnel pour rester compatible avec une coque plus ancienne.
   */
  setPostUrl?: (postUrl: string) => void;
  stop: () => void;
  /**
   * Ouvre les réglages système de l'app (iOS : UIApplication.openSettingsURLString,
   * Android : Intent ACTION_APPLICATION_DETAILS_SETTINGS). Utilisé par l'écran
   * de permission de localisation « Toujours ». Optionnel pour rester
   * compatible avec une coque plus ancienne qui n'exposerait pas la méthode.
   */
  openAppSettings?: () => void;
  /**
   * Android seulement : ouvre l'écran système d'optimisation de la batterie.
   * À proposer quand `getActiveSession()` annonce `batteryRestricted`.
   */
  openBatterySettings?: () => void;
  /**
   * État de la session native, pour une page qui démarre alors qu'un trajet
   * s'enregistre peut-être encore (rechargement, lien profond, WebView
   * relancée par le système). Absent des coques antérieures au 05/10/2026 :
   * TOUJOURS tester `typeof ... === 'function'` avant d'appeler.
   */
  getActiveSession?: () => Promise<KokenLocationSessionInfo>;
  /**
   * Reprend la session native en cours SANS la redémarrer : pose le callback,
   * puis le natif livre dans l'ordre les points gardés pendant qu'aucune page
   * n'écoutait. `start()`, lui, ouvre une session neuve et jette ces points.
   * `active: false` dans la réponse : aucune session à reprendre. Sur iOS, des
   * points gardés d'une session arrêtée faute de page (`orphaned`) peuvent
   * tout de même être livrés une fois par cet appel ; le site ne s'en sert
   * pas encore (limite connue, cf. `resumePersistedSession`).
   */
  resume?: (opts: {
    callback: (data: KokenLocationCallbackData) => void;
  }) => Promise<KokenLocationSessionInfo>;
}

/** Noms d'autorisation, ceux d'iOS. Android n'émet jamais `always` ni `restricted`. */
export type KokenLocationAuthorization =
  | 'always'
  | 'whenInUse'
  | 'denied'
  | 'restricted'
  | 'notDetermined';

/** Dernière erreur native gardée pour une page qui n'écoutait pas. `at` en secondes. */
export interface KokenLocationLastError {
  code: string;
  at?: number;
  [extra: string]: unknown;
}

/**
 * Dernier arrêt que la page n'a pas demandé. `at` en secondes.
 * Motifs connus : `orphaned` (les deux coques), et sur Android `task_removed`,
 * `permission_denied`, `activity_finished`, `start_refused`,
 * `service_destroyed`, `process_killed`.
 */
export interface KokenLocationLastStop {
  reason: string;
  at?: number;
  bufferedCount?: number;
  startedAt?: number;
}

/** Réponse de `getActiveSession()` et de `resume()`. Les temps sont en SECONDES. */
export interface KokenLocationSessionInfo {
  active: boolean;
  /** Une page a-t-elle pris la session (callback posé) ? */
  attached?: boolean;
  startedAt?: number;
  /** Points gardés par le natif, pas encore livrés à une page. */
  bufferedCount?: number;
  /** Points perdus parce que la réserve native était pleine. */
  droppedCount?: number;
  authorization?: KokenLocationAuthorization;
  /**
   * iOS : le mode d'arrière-plan « localisation » est déclaré, donc
   * l'enregistrement continue écran verrouillé avec « Pendant l'utilisation »
   * comme avec « Toujours ». Android : la permission de localisation est tenue.
   */
  backgroundCapable?: boolean;
  lastError?: KokenLocationLastError | null;
  lastStop?: KokenLocationLastStop | null;
  // ── Android seulement ──
  platform?: 'android';
  serviceRunning?: boolean;
  /** Démarrage demandé app masquée : il sera exécuté au retour dans l'app. */
  startPending?: boolean;
  notificationsEnabled?: boolean;
  /** Réglage « Restreinte » : le système coupe le suivi écran éteint. */
  batteryRestricted?: boolean;
  batteryOptimizationExempt?: boolean;
  powerSaveMode?: boolean;
}

/**
 * Événements window émis par les coques récentes (CustomEvent). Une page qui
 * n'écoute pas garde le comportement d'avant.
 */
export type KokenLocationEventName =
  | 'kokenLocation:error'
  | 'kokenLocation:stopped'
  | 'kokenLocation:authorization'
  | 'kokenLocation:discarded';

/**
 * `kokenLocation:error`. Codes : `location_permission_denied`,
 * `location_error`, `missing_background_mode` (iOS), `background_start_blocked`
 * (Android, `recoverable: true` : le démarrage sera fait au retour dans l'app).
 */
export interface KokenLocationErrorDetail {
  code: string;
  authorization?: KokenLocationAuthorization;
  reason?: string;
  recoverable?: boolean;
}

/** `kokenLocation:stopped` : arrêt que la page n'a pas demandé. */
export interface KokenLocationStoppedDetail {
  reason: string;
  bufferedCount?: number;
}

/** `kokenLocation:authorization` : à chaque changement, retour des Réglages compris. */
export interface KokenLocationAuthorizationDetail {
  authorization?: KokenLocationAuthorization;
  backgroundCapable?: boolean;
  // Android seulement
  notificationsEnabled?: boolean;
  batteryRestricted?: boolean;
  batteryOptimizationExempt?: boolean;
  powerSaveMode?: boolean;
}

/** `kokenLocation:discarded` : points gardés puis jetés (`start` ou `stop`). */
export interface KokenLocationDiscardedDetail {
  count?: number;
  reason?: string;
}

// Seule déclaration de `window.kokenLocation` : `medianNativeBridge` la reprend d'ici.
declare global {
  interface Window {
    kokenLocation?: KokenBackgroundLocation;
  }
}

/**
 * Check if the native Kōken GPS bridge is available (injected by the native
 * shell via `window.kokenLocation`). C'est le seul moteur GPS de l'app :
 * si cette fonction retourne `false`, le suivi GPS est indisponible.
 */
export const isKokenLocationAvailable = (): boolean => {
  if (typeof window === 'undefined') return false;
  return !!window.kokenLocation && typeof window.kokenLocation.start === 'function';
};
